import { createStyles, Grid, makeStyles, Tooltip } from '@material-ui/core';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import Stepper from '@material-ui/core/Stepper';
import { GetApp as DownloadIcon } from '@material-ui/icons';
import React, { useMemo, useState } from 'react';

import { htmlToPlainText } from '../../utils';
import { TnButton } from '../TnButton';
import { TnTypography } from '../TnTypography';

import CodeChallenge from './question-types/CodeChallenge';
import MultipleChoice from './question-types/MultipleChoice';
import VideoQuestion from './question-types/VideoQuestion';
import { CodingChallenge, HirevueQuestion, PreScreenInterview } from './types';

export interface PreScreenResultsProps {
  interview: PreScreenInterview | null | undefined;
  codingChallenges: CodingChallenge[];
  isVendor: boolean;
}

const useStyles = makeStyles(() =>
  createStyles({
    nextButton: {
      marginLeft: 16,
    },
    buttonContainer: {
      margin: '16px 0px',
      alignItems: 'center',
    },
    headingContainer: {
      marginBottom: 8,
    },
    step: {
      cursor: 'pointer',
    },
    downloadButton: {
      marginLeft: 16,
      cursor: 'pointer',
    },
  }),
);

const supportedQuestionTypes = ['multiple-choice', 'prog-challenge', 'database-challenge', 'video'];

const vendorSupportedQuestionTypes = ['multiple-choice', 'video'];

const toQuestions = (
  interview: PreScreenInterview | null | undefined,
  codingChallenges: CodingChallenge[],
  isVendor: boolean,
): HirevueQuestion[] => {
  const questionTypes = isVendor ? vendorSupportedQuestionTypes : supportedQuestionTypes;
  const questions: HirevueQuestion[] = [];

  interview?.answers?.forEach((answer: Readonly<any>) => {
    if (questionTypes.includes(answer?.question?.type)) {
      let fullCodingChallenge;
      if (['prog-challenge', 'database-challenge'].includes(answer?.question?.type)) {
        fullCodingChallenge = codingChallenges?.find((challenge) => {
          return challenge.question?.id === answer?.question?.id;
        });
      }
      questions.push({
        text: answer.question.text,
        type: answer.question.type,
        video: answer.video?.downloadUrls?.mp4,
        options: answer.question.options,
        optionPoints: answer.question.points,
        points: answer.points,
        selectedOptionIndex: answer.selectedOptionIndex,
        textAnswer: answer.answer,
        codingChallengeQuestion: fullCodingChallenge?.question,
      });
    }
  });

  return questions;
};

const downloadCodeChallenges = (codingChallenges: CodingChallenge[]) => {
  let fileContents = '';
  codingChallenges.forEach((challenge, index) => {
    const question = challenge.question;
    const answer = challenge.answer;

    if (!question) {
      return;
    }

    fileContents = fileContents.concat(`Question ${index + 1}: ${question.title} \n`);

    if (question.category) {
      fileContents = fileContents.concat(`Category: ${question.category} \n`);
    }

    if (question.languages) {
      const languages = question.languages.map((language: Readonly<any>) => language.name).join(', ');
      fileContents = fileContents.concat(`Language: ${languages} \n\n`);
    }

    if (question.description) {
      const description = htmlToPlainText(question.description);
      fileContents = fileContents.concat(`Description \n${description} \n\n`);
    }

    if (question.input?.description) {
      const input = htmlToPlainText(question.input.description);
      fileContents = fileContents.concat(`Input \n${input} \n\n`);
    }

    if (question.output?.description) {
      const output = htmlToPlainText(question.output.description);
      fileContents = fileContents.concat(`Output \n${output} \n\n`);
    }

    if (question.schema) {
      const schema = htmlToPlainText(question.schema);
      fileContents = fileContents.concat(`Schema \n${schema} \n\n`);
    }

    if (answer) {
      fileContents = fileContents.concat(`Answer \n${answer} \n\n\n`);
    }
  });

  const data = new Blob([fileContents], { type: 'text/plain' });

  const url = window.URL.createObjectURL(data);

  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `CodeResults.txt`);

  // Append to html link element page
  document.body.appendChild(link);

  // Start download
  link.click();

  // Clean up and remove the link
  link.parentNode?.removeChild(link);
};

// Step-through view of a candidate's Hirevue pre-screen answers. Fetching the interview is left to the
// caller so each app can use its own gateway and wrap this in its own modal.
const PreScreenResults: React.FC<PreScreenResultsProps> = ({ interview, codingChallenges, isVendor }) => {
  const classes = useStyles();

  const [activeStep, setActiveStep] = useState<number>(0);

  const questions = useMemo(
    () => toQuestions(interview, codingChallenges, isVendor),
    [interview, codingChallenges, isVendor],
  );
  const steps = questions.map((_, index) => `Question ${index + 1}`);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const renderQuestionContent = () => {
    if (questions?.length > 0) {
      const selectedQuestion = questions[activeStep];
      switch (selectedQuestion?.type) {
        case 'multiple-choice':
          return <MultipleChoice question={selectedQuestion} />;
        case 'prog-challenge':
        case 'database-challenge':
          return <CodeChallenge question={selectedQuestion} />;
        case 'video':
          return <VideoQuestion question={selectedQuestion} />;
      }
    }

    return <></>;
  };

  return (
    <>
      <Grid container justify="center" xs={12}>
        <Stepper activeStep={activeStep}>
          {steps.map((label: string, index: number) => {
            return (
              <Step className={classes.step} key={label} onClick={() => setActiveStep(index)} disabled={false}>
                <StepLabel>{label}</StepLabel>
              </Step>
            );
          })}
        </Stepper>
      </Grid>
      <Grid className={classes.headingContainer} container xs={12} justify="center">
        <TnTypography variant="subtitle">{interview?.candidateFullName ?? ''}</TnTypography>
      </Grid>
      <Grid className={classes.headingContainer} container xs={12} justify="center">
        <TnTypography variant="subtitle">{interview?.positionTitle ?? ''}</TnTypography>
      </Grid>
      <Grid container xs={12} justify="center">
        {renderQuestionContent()}
      </Grid>
      <Grid className={classes.buttonContainer} container xs={12} justify="center">
        <>
          <Grid item>
            <TnButton
              variant="outlined"
              color="primary"
              onClick={handleBack}
              value="Previous Question"
              disabled={activeStep === 0}
            />
          </Grid>
          <Grid className={classes.nextButton}>
            <TnButton
              variant="contained"
              color="primary"
              onClick={handleNext}
              value="Next Question"
              disabled={activeStep === steps.length - 1}
            />
          </Grid>
          {codingChallenges?.length > 0 && !isVendor && (
            <Grid className={classes.downloadButton}>
              <Tooltip title="Download Code Challenge Questions">
                <DownloadIcon
                  className={classes.downloadButton}
                  onClick={() => downloadCodeChallenges(codingChallenges)}
                />
              </Tooltip>
            </Grid>
          )}
        </>
      </Grid>
    </>
  );
};

export default PreScreenResults;
